import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiFacebook, FiLinkedin, FiInstagram, FiHeart } from 'react-icons/fi';

const Footer = () => {
  const quickLinks = [
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Results', href: '#results' },
    { label: 'About', href: '#about' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Contact', href: '#contact' }
  ];

  const legalLinks = [
    { label: 'Terms of Service', href: '/terms' },
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Accessibility', href: '/accessibility' }
  ];

  const socialLinks = [
    { icon: FiFacebook, href: 'https://Facebook.com/337098602826472', label: 'Facebook' },
    { icon: FiLinkedin, href: 'https://www.linkedin.com/in/sanna-darwish-m-s-ccc-slp-b2a3491a8', label: 'LinkedIn' },
    { icon: FiInstagram, href: 'https://www.instagram.com/wethebillions', label: 'Instagram' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId.replace('#', ''));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gray-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <div className="flex items-center gap-3">
              <img
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753416293845-4.png"
                alt="We The Billions Brain Icon"
                className="h-10 w-10"
              />
              <img
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753170072528-v%20lg%20We%20The%20Billions%20logo.png"
                alt="We The Billions"
                className="h-8 w-auto"
              />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              86 billion neurons. 1 unique YOU.
            </p>
            <p className="text-gray-400 text-sm">
              Virtual Sessions (Globally) & In-Person Sessions (MD & VA)
            </p>
            <p className="text-gray-400 text-sm flex items-center gap-1">
              Made with <SafeIcon icon={FiHeart} className="h-4 w-4 text-red-500" /> for every unique brain
            </p>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h3 className="font-bold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-gray-400 hover:text-white transition-colors duration-200 text-sm text-left"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="font-bold text-lg mb-4">Contact</h3>
            <div className="space-y-2 text-sm">
              <div>
                <div className="text-gray-400">Email:</div>
                <a
                  href="mailto:hello@wethebillions.com"
                  className="text-white hover:text-accent-gold transition-colors"
                >
                  hello@wethebillions.com
                </a>
              </div>
              <div>
                <div className="text-gray-400">Phone:</div>
                <a
                  href="tel:+12026560054"
                  className="text-white hover:text-accent-gold transition-colors"
                >
                  (202) 656-0054
                </a>
              </div>
              <div>
                <div className="text-gray-400">Website:</div>
                <a
                  href="https://www.wethebillions.com"
                  className="text-white hover:text-accent-gold transition-colors"
                >
                  www.wethebillions.com
                </a>
              </div>
              <div className="pt-2">
                <a
                  href="https://wa.me/+12026560054"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors"
                >
                  <span>💬</span> WhatsApp
                </a>
              </div>
            </div>
          </motion.div>

          {/* Social & Legal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h3 className="font-bold text-lg mb-4">Follow Us</h3>
            <div className="flex gap-4 mb-6">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-accent-magenta transition-colors duration-200"
                  aria-label={social.label}
                >
                  <SafeIcon icon={social.icon} className="h-5 w-5" />
                </a>
              ))}
            </div>
            <div className="space-y-2">
              {legalLinks.map((link, index) => (
                <div key={index}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-colors duration-200 text-sm block"
                  >
                    {link.label}
                  </a>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="border-t border-gray-800 pt-8 text-center"
        >
          <p className="text-gray-400 text-sm">
            © 2024 We The Billions. All rights reserved. | Executive Function Therapy for Learning Success
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;